import * as React from "react";
import {cx} from "emotion";

import "./zip-code.scss";

import {cs}               from "@Common/react/chain-services";
import {UseState}         from "@Common/react/use-state";
import {Static2}          from "@Common/react/static-2";
import {Form2}            from "@Common/react/cs-form-2/form2";
import {required}         from "@Common/react/cs-form-2/validators/required";
import {bindInput}        from "@Common/react/bind-input";
import {chain}            from "@Common/utils/fs";
import {isDevQuan}        from "@Common/ui-components/debug/is-dev-quan";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}   from "@Common/ui-components/light/scroll-into-view";

export const ZipCode = ({next}) => cs(
    ["apiError", (_, next) => UseState({next})],
    ["data", ({apiError}, next) => UseState({
        initValue: !isDevQuan() ? {
            zipCodes: ""
        } : {
            zipCodes: "76110,76137"
        },
        next: ({value, onChange}) => next({
            value,
            onChange: (v)=>{
                onChange(v);
                if (apiError.value && v.zipCodes !== value.zipCodes) {
                    apiError.onChange(null);
                }
            }
        }),
    })],
    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],
    ["form", ({data}, next) => Form2({
        data,
        showErrors: true,
        fields: [
            {
                path: ["zipCodes"],
                validators: [
                    required,
                    {
                        name: "zipFormat",
                        validate: (value) => {
                            const zipCodes = value?.trim()?.split(/[,\s]+/)?.filter(v=>v);
                            return !zipCodes.some(zipCode => isNaN(zipCode) || zipCode.length !== 5);
                        },
                    }
                ],
            }
        ],
        next,
    })],
    ({form, eh, domRef, apiError, data}) => next({
        valid: form.valid,
        getSearchRequest: () => data.value,
        highlightErrors: () => {
            eh.start();
            scrollIntoView(domRef.get());
        },
        handleApiError: (error) => {
            apiError.onChange({error});
            eh.start();
            scrollIntoView(domRef.get());
        },
        clearApiError: () => apiError.onChange(null),
        render: ({showErrors}) => (
            <div className="zip-code-pb4">
                <div className="label" xd-qa-type="label" xd-qa-name="New Homeowners Enter Zip Codes">
                    Enter zip codes you want to target
                </div>

                {chain(
                    form.field("zipCodes"),
                    (field) => ({
                        ...field,
                        showError: showErrors.value.includes("all") || showErrors.value.includes("zipCodes"),
                    }),
                    (field) => <>
                        <input {...{
                            ref: domRef.set,
                            className: cx({hasError: field.showError && field.error}, eh.css),
                            placeholder: "Separate multiple zip codes with a comma",
                            ...bindInput(field.state),
                            onBlur: () => {
                                if (!showErrors.value.includes("zipCodes") && !!field.error) {
                                    showErrors.onChange([...showErrors.value, "zipCodes"]);
                                }
                                field.state.change((zipCodes) => chain(
                                    zipCodes,
                                    (_) => _.trim(),
                                    (_) => _?.split(/[,\s]+/),
                                    (_) => _?.filter(v=>v),
                                    (_) => _?.join(",")
                                ));
                            },
                            "xd-qa-type": "text_field",
                            "xd-qa-name": "New Homeowners Enter Zip Codes"
                        }}/>
                        {field.showError && field.error?.failedValidator?.name === "zipFormat" && (
                            <div className="error">
                                One or more zip codes are invalid
                            </div>
                        )}
                    </>
                )}

                {apiError.value && (
                    <div className="error" xd-qa-name="New Homeowners By Zip Code Error" xd-qa-type="label">
                        {apiError.value.error}
                    </div>
                )}

                <div className={cx("clear", {apiError: apiError.value})}>
                    <a {...{
                        onClick: () => data.onChange({
                            zipCodes: ""
                        }),
                        "xd-qa-type": "link",
                        "xd-qa-name": "New Homeowners Clear Zip Codes",
                    }}>
                        Clear Zip Codes
                    </a>
                </div>
            </div>
        ),
    }),
);

